import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT_LAYOUT } from '../core/Config';
import { GameSettings } from '../core/GameSettings';
import { pulseVirtualActions } from '../core/VirtualControls';

export class DialogueUI {
  private readonly scene: Phaser.Scene;
  private readonly container: Phaser.GameObjects.Container;
  private readonly title: Phaser.GameObjects.Text;
  private readonly body: Phaser.GameObjects.Text;
  private readonly hint: Phaser.GameObjects.Text;
  private readonly nextMarker: Phaser.GameObjects.Text;
  private readonly portraitImage: Phaser.GameObjects.Image;
  private readonly portraitDecor: Phaser.GameObjects.Rectangle[];
  private readonly bodyWithPortraitX: number;
  private readonly bodyWithoutPortraitX: number;
  private readonly bodyWithPortraitWidth: number;
  private readonly bodyWithoutPortraitWidth: number;
  private fullText = '';
  private visibleLength = 0;
  private revealAccumulator = 0;
  private fullyRevealed = true;
  private opened = false;
  private readonly baseBodyY: number;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const mobile = IS_PORTRAIT_LAYOUT;
    const panelWidth = mobile ? GAME_WIDTH - 48 : GAME_WIDTH - 72;
    const panelHeight = mobile ? 410 : 220;
    const panelY = GAME_HEIGHT - panelHeight / 2 - (mobile ? 24 : 20);
    const panelTop = panelY - panelHeight / 2;
    const portraitX = mobile ? 132 : 126;
    const portraitY = mobile ? panelY + 18 : panelY - 18;
    const portraitWidth = mobile ? 166 : 120;
    const portraitHeight = mobile ? 206 : 144;
    this.baseBodyY = mobile ? panelTop + 112 : GAME_HEIGHT - 182;
    this.bodyWithPortraitX = mobile ? 244 : 240;
    this.bodyWithoutPortraitX = mobile ? 58 : 92;
    this.bodyWithPortraitWidth = mobile ? panelWidth - 286 : GAME_WIDTH - 360;
    this.bodyWithoutPortraitWidth = mobile ? panelWidth - 92 : GAME_WIDTH - 184;

    const shadow = scene.add
      .rectangle(GAME_WIDTH / 2 + 8, panelY + 10, panelWidth, panelHeight, 0x000000, 0.32)
      .setOrigin(0.5);
    const bgOuter = scene.add
      .rectangle(GAME_WIDTH / 2, panelY, panelWidth, panelHeight, 0x172019, 0.98)
      .setStrokeStyle(mobile ? 5 : 4, 0xe8c768, 0.96)
      .setInteractive({ useHandCursor: false })
      .on('pointerdown', () => pulseVirtualActions(['confirm', 'interact']));
    const bgInner = scene.add
      .rectangle(GAME_WIDTH / 2, panelY, panelWidth - 26, panelHeight - 26, 0x0c120f, 0.97)
      .setStrokeStyle(2, 0x8ebc98, 0.72);
    const portraitBackdrop = scene.add
      .rectangle(portraitX, portraitY, portraitWidth + 18, portraitHeight + 18, 0x2f4938, 0.72);
    const portraitFrame = scene.add
      .rectangle(portraitX, portraitY, portraitWidth, portraitHeight, 0x111713, 0.98)
      .setStrokeStyle(3, 0xe8c768, 0.9);
    this.portraitImage = scene.add
      .image(portraitX, portraitY + 5, 'npc_village')
      .setDisplaySize(mobile ? 138 : 96, mobile ? 178 : 122)
      .setVisible(false);
    this.portraitDecor = [portraitBackdrop, portraitFrame];

    const titleX = mobile ? 244 : 194;
    const titlePlateWidth = mobile ? 360 : 196;
    const titlePlate = scene.add
      .rectangle(titleX + titlePlateWidth / 2, panelTop + (mobile ? 58 : 30), titlePlateWidth, mobile ? 64 : 42, 0x304133, 0.98)
      .setStrokeStyle(2, 0xe8c768, 0.82);
    this.title = scene.add.text(titleX + 18, panelTop + (mobile ? 37 : 13), '', {
      fontSize: mobile ? '34px' : '24px',
      color: '#fff0b5',
      fontStyle: 'bold',
    });
    this.body = scene.add.text(this.bodyWithPortraitX, this.baseBodyY, '', {
      fontSize: mobile ? '35px' : '27px',
      color: '#f4f6f0',
      wordWrap: { width: this.bodyWithPortraitWidth },
      lineSpacing: mobile ? 13 : 10,
    });
    this.hint = scene.add
      .text(GAME_WIDTH - (mobile ? 242 : 280), GAME_HEIGHT - (mobile ? 76 : 46), mobile ? 'A  다음' : 'SPACE / ENTER', {
        fontSize: mobile ? '27px' : '18px',
        color: '#b9ddbf',
        fontStyle: 'bold',
      });
    this.nextMarker = scene.add
      .text(GAME_WIDTH - (mobile ? 72 : 86), GAME_HEIGHT - (mobile ? 84 : 52), '>', {
        fontSize: mobile ? '34px' : '22px',
        color: '#ffe391',
        fontStyle: 'bold',
      });

    this.container = scene.add
      .container(0, 0, [
        shadow,
        bgOuter,
        bgInner,
        portraitBackdrop,
        portraitFrame,
        this.portraitImage,
        titlePlate,
        this.title,
        this.body,
        this.hint,
        this.nextMarker,
      ])
      .setScrollFactor(0)
      .setDepth(10000)
      .setVisible(false);

    scene.tweens.add({ targets: this.hint, alpha: { from: 0.55, to: 1 }, duration: 760, yoyo: true, repeat: -1 });
    scene.tweens.add({ targets: this.nextMarker, y: '+=7', duration: 540, yoyo: true, repeat: -1, ease: 'sine.inOut' });
  }

  show(speaker: string | undefined, text: string, portraitKey?: string, instant = false) {
    this.title.setText(speaker ?? '대화');
    this.fullText = text;
    this.visibleLength = instant ? text.length : 0;
    this.revealAccumulator = 0;
    this.fullyRevealed = instant;
    this.body.setText(instant ? text : '');

    const nextKey = portraitKey && this.scene.textures.exists(portraitKey) ? portraitKey : undefined;
    const hasPortrait = Boolean(nextKey);
    this.portraitDecor.forEach((item) => item.setVisible(hasPortrait));
    if (nextKey) {
      this.portraitImage
        .setTexture(nextKey)
        .setDisplaySize(IS_PORTRAIT_LAYOUT ? 138 : 96, IS_PORTRAIT_LAYOUT ? 178 : 122)
        .setVisible(true);
    } else {
      this.portraitImage.setVisible(false);
    }
    this.body
      .setX(hasPortrait ? this.bodyWithPortraitX : this.bodyWithoutPortraitX)
      .setWordWrapWidth(hasPortrait ? this.bodyWithPortraitWidth : this.bodyWithoutPortraitWidth);

    this.container.setVisible(true);
    this.open();
    this.refreshHint();
  }

  update(deltaMs: number) {
    if (!this.container.visible || this.fullyRevealed) return;
    this.revealAccumulator += deltaMs;
    const perCharMs = GameSettings.getTextSpeedMs();
    while (this.revealAccumulator >= perCharMs && !this.fullyRevealed) {
      this.revealAccumulator -= perCharMs;
      this.visibleLength = Math.min(this.visibleLength + 1, this.fullText.length);
      this.body.setText(this.fullText.slice(0, this.visibleLength));
      if (this.visibleLength >= this.fullText.length) this.fullyRevealed = true;
    }
    this.refreshHint();
  }

  revealAll() {
    this.visibleLength = this.fullText.length;
    this.fullyRevealed = true;
    this.body.setText(this.fullText);
    this.refreshHint();
  }

  isFullyRevealed() {
    return this.fullyRevealed;
  }

  isVisible() {
    return this.container.visible;
  }

  hide() {
    this.container.setVisible(false);
  }

  private open() {
    if (!GameSettings.get('uiMotion')) return;
    if (!this.opened) {
      this.container.setAlpha(0);
      this.container.setY(24);
      this.opened = true;
    }
    this.scene.tweens.killTweensOf(this.container);
    this.scene.tweens.add({ targets: this.container, alpha: 1, y: 0, duration: 180, ease: 'quad.out' });
    this.scene.tweens.killTweensOf(this.body);
    this.body.setY(this.baseBodyY + 10);
    this.scene.tweens.add({ targets: this.body, y: this.baseBodyY, duration: 180, ease: 'quad.out' });
  }

  private refreshHint() {
    if (IS_PORTRAIT_LAYOUT) {
      this.hint.setText(this.fullyRevealed ? '대화창 터치  다음' : '대화창 터치  전체 보기');
    } else {
      this.hint.setText(this.fullyRevealed ? 'SPACE / ENTER  다음' : 'SPACE / ENTER  전체 표시');
    }
    this.nextMarker.setVisible(this.fullyRevealed);
  }
}
